import { Autocomplete, TextField } from "@mui/material";
import {
  FREQ_LIST,
  getRRuleFrequency,
  MONTHDAYS,
  WEEKDAYS,
} from "components/shared/biz/utils/rruleUtils";
import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { OptionPair, RepeatEntity } from "types/todolist";

interface RepeatWidgetProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  entity: Partial<RepeatEntity>;
  styles?: CSSProperties;
}

interface RepeatWidgetRef {
  getRuleOptions: () => any;
}

const RepeatWidget = forwardRef<RepeatWidgetRef, RepeatWidgetProps>(
  ({ control, setValue, entity, styles }, ref) => {
    const [formFields, setFormFields] = useState<RepeatEntity>({
      frequency: entity.frequency || {
        name: "frequency",
        required: true,
        value: "weekly",
        disabled: false,
      },
      interval: entity.interval || {
        name: "interval",
        required: true,
        value: 1,
        disabled: false,
      },
      weekdays: entity.weekdays || {
        name: "weekday",
        required: false,
        value: [],
        disabled: false,
      },
      monthdays: entity.monthdays || {
        name: "monthdays",
        required: false,
        value: [],
        disabled: false,
      },
    });

    const [mdays, setMdays] = useState<OptionPair[]>(entity.monthdays?.value || []);
    const [wdays, setWdays] = useState<OptionPair[]>(entity.weekdays?.value || []);

    useImperativeHandle(ref, () => ({
      getRuleOptions: () => {
        const rruleOption: any = {
          freq: getRRuleFrequency(formFields.frequency.value || "weekly"),
          interval: formFields.interval.value || 1,
        };
        if (formFields.frequency.value === "weekly") {
          rruleOption.byweekday = (formFields.weekdays.value || []).map((day: any) => day.value);
        }
        if (formFields.frequency.value === "monthly") {
          rruleOption.bymonthday = (formFields.monthdays.value || []).map((day: any) => day.value);
        }
        return rruleOption;
      },
    }));

    useEffect(() => {
      if (formFields.frequency) {
        setValue(formFields.frequency.name || "frequency", formFields.frequency.value);
      }
      if (formFields.interval) {
        setValue(formFields.interval.name || "interval", formFields.interval.value);
      }
    }, [formFields]);

    return (
      <div
        style={{
          ...styles,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <Controller
            name={formFields.interval.name || "interval"}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="number"
                variant="outlined"
                size="small"
                error={!!fieldState.error}
                helperText={fieldState?.error?.message}
                inputProps={{
                  min: 1,
                  max: 100,
                  step: 1,
                }}
                value={formFields.interval.value}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  let safeValue = !e.target.value || isNaN(v) || v < 1 ? 1 : v;

                  safeValue = safeValue > 100 ? 100 : safeValue;
                  field.onChange(e);
                  setFormFields((prev) => ({
                    ...prev,
                    interval: {
                      ...prev.interval,
                      value: safeValue,
                    },
                  }));
                }}
                onKeyDown={(e) => {
                  if (e.key === "." || e.key === "e") e.preventDefault();
                }}
                disabled={formFields.interval.disabled}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "40px",
                    borderRadius: "8px",
                  },
                  "& .MuiInputBase-input": {
                    height: "40px",
                    boxSizing: "border-box",
                    padding: "0 14px",
                  },
                }}
              />
            )}
            rules={{
              required: formFields?.interval.required,
            }}
          />
          <Controller
            name={formFields.frequency.name || "frequency"}
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                disableClearable
                value={FREQ_LIST.find((o) => o.key === formFields.frequency.value)}
                options={FREQ_LIST}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        height: "40px",
                        boxSizing: "border-box",
                        padding: "0 14px",
                      },
                    }}
                  />
                )}
                onChange={(event, newValue) => {
                  field.onChange(newValue);
                  setFormFields((prev) => ({
                    ...prev,
                    frequency: {
                      ...prev.frequency,
                      value: newValue?.key || "",
                    },
                  }));
                }}
                disabled={formFields.frequency.disabled}
              />
            )}
          />
        </div>
        {formFields.frequency.value !== "daily" && (
          <div>
            <span>On</span>
            <div>
              {formFields.frequency.value === "weekly" && (
                <Controller
                  control={control}
                  name="weekdays"
                  defaultValue={wdays}
                  render={({ field, fieldState }) => (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(7, 1fr)",
                          placeItems: "center",
                          gap: 4,
                        }}
                      >
                        {WEEKDAYS.map((day) => {
                          const selected = field.value
                            .map((v: OptionPair) => v.key)
                            ?.includes(day.key);
                          return (
                            <div
                              key={`weekday-${day.key}`}
                              onClick={
                                formFields.weekdays.disabled
                                  ? undefined
                                  : () => {
                                      const newValue = selected
                                        ? field.value.filter((v: OptionPair) => v.key !== day.key)
                                        : [...field.value, day];
                                      field.onChange(newValue);
                                      setWdays(newValue);
                                    }
                              }
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 10,
                                border: "1px solid #ddd",
                                textAlign: "center",
                                lineHeight: "40px",
                                fontSize: 14,
                                cursor: formFields.weekdays.disabled ? "not-allowed" : "pointer",
                                backgroundColor: selected ? "#030213" : "transparent",
                                color: selected ? "#fff" : "inherit",
                              }}
                            >
                              {day.label.substring(0, 3)}
                            </div>
                          );
                        })}
                      </div>
                      {fieldState.error ? (
                        <p style={{ color: "red" }}>{fieldState.error.message}</p>
                      ) : null}
                    </>
                  )}
                  rules={{
                    required: {
                      value: formFields.frequency.required,
                      message: "Please select at least one day",
                    },
                  }}
                />
              )}
              {formFields.frequency.value === "monthly" && (
                <Controller
                  control={control}
                  name="monthdays"
                  defaultValue={mdays}
                  render={({ field, fieldState }) => (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(7, 1fr)",
                          placeItems: "center",
                          gap: 4,
                        }}
                      >
                        {MONTHDAYS.map((day) => {
                          const selected = field.value
                            .map((v: OptionPair) => v.key)
                            ?.includes(day.key);
                          return (
                            <div
                              key={`monthday-${day.key}`}
                              onClick={
                                formFields.monthdays.disabled
                                  ? undefined
                                  : () => {
                                      const newValue = selected
                                        ? field.value.filter((v: OptionPair) => v.key !== day.key)
                                        : [...field.value, day];
                                      field.onChange(newValue);
                                      setMdays(newValue);
                                    }
                              }
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 10,
                                border: "1px solid #ddd",
                                textAlign: "center",
                                lineHeight: "40px",
                                fontSize: 14,
                                cursor: formFields.monthdays.disabled ? "not-allowed" : "pointer",
                                backgroundColor: selected ? "#030213" : "transparent",
                                color: selected ? "#fff" : "inherit",
                              }}
                            >
                              {day.label.substring(0, 3)}
                            </div>
                          );
                        })}
                      </div>
                      {fieldState.error ? (
                        <p style={{ color: "red" }}>{fieldState.error.message}</p>
                      ) : null}
                    </>
                  )}
                  rules={{
                    required: {
                      value: formFields.frequency.required,
                      message: "Please select at least one day",
                    },
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default RepeatWidget;
